/**
 * Where the hand at the sauna door is, this frame.
 *
 * `Dance.ts`'s indoor cousin and split out for the same reasons: it takes
 * seconds and returns a pose, it imports no three.js, it holds no scene
 * objects, and it can be stepped a thousand times in a test without a canvas.
 * `World` owns the man; this owns what his arm is doing.
 *
 * The one thing it does not share with `Dance` is its clock. The crowd in the
 * tent is on wall-clock time because the set started before the page loaded;
 * this is an answer to a press, so it runs on simulation seconds - which is
 * what keeps the hand where it is behind a pause menu instead of running it
 * out while nobody is looking. See `Game.render`.
 *
 * It writes to nothing. The door was already locked and stays locked whatever
 * this returns - see src/world/Sauna.ts.
 */

import { GREETING } from '../config/gameplay';
import { lerp, smoothStep } from '../utils/math';

/**
 * One frame of arm, in the three angles `World.poseGreetingArm` turns into a
 * rotation. Radians throughout, and read off the man rather than off the
 * world: `splay` goes out to whichever side the arm being posed is on.
 */
export interface GreetingPose {
  /** How far the upper arm is above horizontal-forward. */
  lift: number;
  /** How far round towards his own side it has swung. */
  splay: number;
  /** The fold left in the elbow. Negative brings the forearm up in front. */
  elbow: number;
}

/**
 * An arm hanging, which is where every greeting starts and ends.
 *
 * `lift` is a quarter turn *below* horizontal rather than zero, because that
 * is what an arm pointing at the floor is: `World` builds the arm group
 * hanging straight down, so a rest pose has to come out of the same arithmetic
 * as every other pose or the blend between them would not be a blend.
 */
export const GREETING_REST: GreetingPose = { lift: -Math.PI / 2, splay: 0, elbow: 0 };

/** And where it gets to. Authored in `GREETING`, for the reasons written there. */
export const GREETING_UP: GreetingPose = {
  lift: GREETING.lift,
  splay: GREETING.splay,
  elbow: GREETING.elbow,
};

/** Up, held, and down again. */
export const GREETING_DURATION = GREETING.raise + GREETING.hold + GREETING.drop;

/**
 * The arm `seconds` after the handle was tried, or null when there is nothing
 * to pose - before it starts, and once it is over.
 *
 * Null rather than `GREETING_REST` at the far end so that a man nobody has
 * spoken to costs nothing per frame: the caller writes the rest pose once, on
 * the frame this stops answering, and then leaves him alone. Trying the handle
 * again restarts it, because he is the one man in the game allowed to repeat
 * himself - see `KEMPF_LINE`.
 *
 * Both ramps are smoothed at both ends. An arm is a limb rather than a lever:
 * it does not leave rest at full speed and it does not arrive at the top and
 * stop dead, and a linear ramp on a five-second hold is very easy to see.
 */
export function greetingPose(seconds: number): GreetingPose | null {
  if (seconds < 0 || seconds >= GREETING_DURATION) return null;
  if (seconds < GREETING.raise) {
    return blend(GREETING_REST, GREETING_UP, smoothStep(seconds / GREETING.raise));
  }
  if (seconds < GREETING.raise + GREETING.hold) return GREETING_UP;
  const back = (seconds - GREETING.raise - GREETING.hold) / GREETING.drop;
  return blend(GREETING_UP, GREETING_REST, smoothStep(back));
}

function blend(from: GreetingPose, to: GreetingPose, t: number): GreetingPose {
  return {
    lift: lerp(from.lift, to.lift, t),
    splay: lerp(from.splay, to.splay, t),
    elbow: lerp(from.elbow, to.elbow, t),
  };
}
